import * as Ramda from 'ramda';
import {
  moneyFormat,
  moneyFormat2Decimals,
} from '../../../../../utils/appUtils';
import {
  type CoverageVersion,
  type GlobalPropertyNames,
  type Prime100QuotePageState,
  GlobalPropertyKeys,
} from '../types';
import { BoostRaterVersionResponse } from '../api/types';
import type useGetCalculator from '../hooks/useGetCalculator';
import { formatGlobalValues } from '../transformers/globalValues';

export { formatGlobalValues };

export function isRecordIntact(data: Record<string, unknown>) {
  const isNotNil = Ramda.complement(Ramda.isNil);
  return Object.values(data).every(isNotNil);
}

const currencyOptionFormatter = (option: number) => {
  return {
    label: moneyFormat(option),
    value: option,
  };
};

const waitingPeriodOptionFormatter = (option: number) => {
  return {
    label: `${option} Hours`,
    value: option,
  };
};

const retroActivePeriodOptionFormatter = (option: number) => {
  return {
    label: P100_RETRO_MAPPING[option as keyof typeof P100_RETRO_MAPPING],
    value: option,
  };
};

const GLOBAL_OPTION_FORMATTERS = {
  [GlobalPropertyKeys.waitingPeriod]: waitingPeriodOptionFormatter,
  [GlobalPropertyKeys.retroactivePeriod]: retroActivePeriodOptionFormatter,
};

export const deriveOptionFormatter = (
  key: GlobalPropertyNames = GlobalPropertyKeys.limit
) => {
  if (key in GLOBAL_OPTION_FORMATTERS) {
    return GLOBAL_OPTION_FORMATTERS[
      key as keyof typeof GLOBAL_OPTION_FORMATTERS
    ];
  }

  return currencyOptionFormatter;
};

export const P100_RETRO_MAPPING = Object.freeze({
  3: 'Full Prior Acts',
  2: '2 Years',
  1: '1 Year',
});

export const getCoverageVersionData = (
  versions: CoverageVersion[],
  boostRaterVersion: BoostRaterVersionResponse
) => {
  const versionOfCoverage =
    versions.find((ver) => ver.version === boostRaterVersion) || versions[0];

  return {
    name: versionOfCoverage.name,
    tooltip: versionOfCoverage.tooltip,
    ordinal: versionOfCoverage.ordinal,
  };
};

export function getP100QuoteEffectiveDateForRaterPayload(
  quoteDetails: Record<string, unknown> | undefined | null
): string | undefined {
  if (quoteDetails == null) {
    return undefined;
  }

  const initialRequest = quoteDetails.initialRequestData as
    | Record<string, unknown>
    | undefined;
  const calculatorGenerated = quoteDetails.calculatorGeneratedData as
    | Record<string, unknown>
    | undefined;

  const raw =
    quoteDetails.effectiveDate ??
    initialRequest?.effectiveDate ??
    calculatorGenerated?.effectiveDate;

  if (raw == null || raw === '') {
    return undefined;
  }

  const parsed = new Date(
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
      ? raw.trim()
      : String(raw)
  );

  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  return parsed.toISOString().slice(0, 10);
}

/**
 * Narrows a raw string from the API to a typed BoostRaterVersionResponse.
 * Returns undefined for missing, empty, or unrecognised values.
 */
export function parseBoostRaterVersion(
  value: string | null | undefined
): BoostRaterVersionResponse | undefined {
  switch (value) {
    case BoostRaterVersionResponse.V4:
      return BoostRaterVersionResponse.V4;
    case BoostRaterVersionResponse.V5:
      return BoostRaterVersionResponse.V5;
    case BoostRaterVersionResponse.V6:
      return BoostRaterVersionResponse.V6;
    default:
      return undefined;
  }
}

/**
 * Resolve P100 V5/V6 from mothership fields on a quote or policy.
 * Prefer `boostRaterVersion` ("V5"/"V6"); fall back to numeric `productVersion`
 * (1 -> V5, 2 -> V6) used on quote search rows.
 */
export function resolveP100RaterVersionFromEntity(
  entity:
    | {
        boostRaterVersion?: string | null;
        productVersion?: number | null;
      }
    | null
    | undefined
): BoostRaterVersionResponse | undefined {
  if (entity == null) {
    return undefined;
  }

  const fromString = parseBoostRaterVersion(entity.boostRaterVersion);
  if (fromString != null) {
    return fromString;
  }

  if (entity.productVersion === 1) {
    return BoostRaterVersionResponse.V5;
  }
  if (entity.productVersion === 2) {
    return BoostRaterVersionResponse.V6;
  }

  return undefined;
}

export const sortAscendingOrder = (a: number, b: number): number => a - b;

/**
 * Injects sub-coverage rows for V6 placeholder IDs that have no label of their own.
 * Used by both the policyholder and admin coverage table builders.
 */
export function buildSubCoverageRows(
  coverages: any,
  enumId: string,
  index: number
): {
  coverageName: string;
  order: number;
  limit?: string | null;
  deductible?: string | null;
  checked?: boolean;
}[] {
  switch (enumId) {
    case 'contingentBusinessIncomeSF':
      if (coverages?.contingentBusinessIncomeSecurityFailureEndorsement)
        return [
          {
            coverageName: 'Contingent Business Income - Security Failure',
            order: index,
            checked: true,
          },
        ];
      break;

    case 'contingentBusinessIncomeSFIT':
      if (coverages?.contingentBusinessIncomeSystemFailureItEndorsement)
        return [
          {
            coverageName: 'Contingent Business Income - System Failure (IT)',
            order: index,
            checked: true,
          },
        ];
      break;

    case 'fundsTransferHeldFunds':
      if (
        coverages?.fundsTransferFraudAndSocialEngineeringWithClientHeldFundsEndorsement
      )
        return [
          {
            coverageName: 'Enhancement to Include Client Held Funds',
            order: index,
            checked: true,
          },
        ];
      break;

    default:
      break;
  }

  return [];
}

export const getIndicatedPricing = (
  calculatorQuery: ReturnType<typeof useGetCalculator>
) => {
  if (calculatorQuery.isFetching || calculatorQuery.isLoading) {
    return 'Calculating';
  }

  if (calculatorQuery.isError || !calculatorQuery.data) {
    return 'Error';
  }

  return moneyFormat2Decimals(calculatorQuery.data.totalPremium);
};

export const formatAndSynchronizeGlobals = (
  coverageValues: Prime100QuotePageState['coverageValues'] | undefined,
  currGlobalValues: Prime100QuotePageState['globalValues']
) => {
  const formattedGlobals = formatGlobalValues(coverageValues);

  return Object.keys(currGlobalValues).reduce((acc, key) => {
    const currentValue =
      currGlobalValues[key as keyof typeof currGlobalValues].value;
    const nextOptions =
      formattedGlobals[key as keyof typeof formattedGlobals].options;
    const eligibilityDefault =
      formattedGlobals[key as keyof typeof formattedGlobals].value;
    const resolvedValue = nextOptions.includes(currentValue)
      ? currentValue
      : nextOptions.includes(eligibilityDefault)
      ? eligibilityDefault
      : nextOptions[0];
    return {
      ...acc,
      [key]: {
        options: nextOptions,
        value: resolvedValue,
      },
    };
  }, formattedGlobals);
};
